import { zodResolver } from "@hookform/resolvers/zod";
import { CirclePlus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { forwardRef, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import { type OptionItem, OptionType, useGetOptionsQuery } from "@/api/admin";
import {
  CustomButton,
  CustomCardBody,
  ErrorForm,
  LabelWithOptional,
  Loader,
  SimpleText,
  Subtitle,
} from "@/components/atoms";
import { InputControl } from "@/components/molecules";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { mockInstagramConnect } from "@/modules/mocksData";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import type { FormProps, FormRefType } from "@/types/common";
import { commonIconProps20 } from "@/utils/constants";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";
import { wordToUpper } from "@/utils/wordToUpper";

import ConnectedAccount from "./components/ConnectedAccount";
import SocialMedia from "./components/SocialMedia";
import { type BrandContactsType, FormSchema } from "./types";

function getAvailableSocialItems(
  fields: { profile: string }[],
  currentProfile: string,
  itemsList: OptionItem[]
) {
  const selectedProfiles = fields.map(f => f.profile).filter(Boolean);
  return itemsList.filter(
    item => !selectedProfiles.includes(item?.id) || item.id === currentProfile
  );
}

type BrandFormContactsProps = FormProps<BrandContactsType> & {
  draftEntityId?: string;
};

const BrandFormContactsInner = forwardRef<
  FormRefType<BrandContactsType>,
  BrandFormContactsProps
>((props, ref) => {
  const {
    defaultValues,
    onSubmit,
    onValidityChange,
    id,
    classes,
    draftEntityId,
  } = props;
  const [isConnected, setConnection] = useState(
    !!defaultValues?.connectedAccounts?.length
  );
  const [isError, setError] = useState(false);

  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.SocialMediaTypes,
  ]);

  const MAX_LINKS = optionsData?.socialMediaTypes?.length || 5;

  const socialOptions = (optionsData?.socialMediaTypes ?? []).map(item => ({
    id: item,
    name: wordToUpper(item),
  }));

  const form = useForm<BrandContactsType>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      ...defaultValues,
      socialLinks: defaultValues?.socialLinks?.length
        ? defaultValues.socialLinks
        : [{ type: "", url: "" }],
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useWizardStepDraft({
    form,
    wizard: "brand",
    entityId: draftEntityId,
    step: "contacts",
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid },
  } = form;

  useExposeFormValues(ref, form);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "socialLinks",
  });

  const connectedAccounts = watch("connectedAccounts") || [];

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const toggleConnection = () => {
    setConnection(false);
    setError(false);
    setValue("connectedAccounts", []);
  };

  const handleInstagramConnect = async () => {
    setError(false);
    const result = await mockInstagramConnect();
    if (result.success && result.token) {
      setConnection(true);
      setValue("connectedAccounts", [...connectedAccounts, result.token]);
    } else {
      setError(true);
    }
  };

  if (isOptionPending) {
    return <Loader />;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(values => {
          const filteredLinks = (values.socialLinks ?? []).filter(
            ({ type, url }) => Boolean(type?.trim() || url?.trim())
          );

          const payload: BrandContactsType = {
            ...values,
            socialLinks: filteredLinks,
          };
          onSubmit(payload);
        })}
        id={id}
        data-testid="brand-form-contacts"
      >
        <CustomCardBody classes={cn("pb-6", classes)}>
          <div className="space-y-6 mb-2.5">
            <Subtitle title="General" />
            <div className="parent">
              <div className="responsive-fields">
                <InputControl<BrandContactsType>
                  formControl={control}
                  label="Email"
                  nameField="email"
                  placeholder="example@email.com"
                  typeField="email"
                  data-testid="brand-form-contacts-email"
                />
                <InputControl<BrandContactsType>
                  formControl={control}
                  label={<LabelWithOptional label="Website" />}
                  nameField="websiteUrl"
                  placeholder="https"
                  data-testid="brand-form-contacts-website"
                />
              </div>
            </div>
          </div>

          <div className={cn(isConnected ? "mb-[30px]" : "mb-[32px]")}>
            <Subtitle title="Instagram integration" classes="mb-1" />
            <SimpleText classes={cn(isConnected ? "mb-6" : "mb-5")}>
              To display your Instagram Reels in the app, link your Instagram
              Business account. Your Instagram account must be linked to a
              Facebook Page.
            </SimpleText>
            {isConnected ? (
              <ConnectedAccount setConnection={toggleConnection} />
            ) : (
              <CustomButton
                onClick={handleInstagramConnect}
                type="button"
                data-testid="brand-form-contacts-instagram-login"
              >
                Log In to Instagram
              </CustomButton>
            )}

            {isError && (
              <ErrorForm message="Could not connect to Instagram. Please try again." />
            )}
          </div>

          <div className="max-w-[588px]">
            <div className="space-y-6 mb-[14px]">
              <Subtitle title="Social media" classes="mb-5" />
              <div className="flex flex-col gap-5">
                {fields.map((elem, index) => {
                  const availableItems = getAvailableSocialItems(
                    fields.map(f => ({ profile: f.type || "" })),
                    elem.type || "",
                    socialOptions
                  );

                  return (
                    <SocialMedia
                      key={elem.id}
                      form={form}
                      elem={elem}
                      index={index}
                      list={availableItems}
                      onRemove={() => remove(index)}
                    />
                  );
                })}
              </div>
            </div>

            <CustomButton
              size="sm"
              type="button"
              variant="link"
              className="relative left-[-14px]"
              onClick={() => {
                if (fields.length < MAX_LINKS) {
                  append({
                    type: "",
                    url: "",
                  });
                }
              }}
              disabled={fields.length >= MAX_LINKS}
              data-testid="brand-form-contacts-social-add-button"
            >
              <CirclePlus {...commonIconProps20} color="#155FCC" />
              Add social
            </CustomButton>
          </div>
        </CustomCardBody>
      </form>
    </Form>
  );
});

BrandFormContactsInner.displayName = "BrandFormContacts";

const BrandFormContacts = observer(BrandFormContactsInner);
export default BrandFormContacts;
